import React, { useState } from 'react';
import { PageLayout } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  subscribeManyEpisodes,
  setEpisodes,
  updateEpisodes,
  deleteEpisodes,
} from '@/lib/collections/episodes';
import type { EpisodesResponse } from '@/lib/collections/episodes';
import { Time } from '@/lib/db-client';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Film,
  Save,
  X,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
} from 'lucide-react';

// ─── Utilities ────────────────────────────────────────────────────────────────

function generateId(): string {
  return `ep-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Form State ───────────────────────────────────────────────────────────────

interface EpisodeFormState {
  title: string;
  description: string;
  thumbnailUrl: string;
  videoUrl: string;
  season: string;
  episodeNumber: string;
  published: boolean;
}

const emptyForm = (): EpisodeFormState => ({
  title: '',
  description: '',
  thumbnailUrl: '',
  videoUrl: '',
  season: '1',
  episodeNumber: '1',
  published: false,
});

// ─── Episode Form ─────────────────────────────────────────────────────────────

const EpisodeForm: React.FC<{
  initial?: EpisodeFormState;
  onSubmit: (data: EpisodeFormState) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
  isLoading: boolean;
}> = ({ initial, onSubmit, onCancel, submitLabel, isLoading }) => {
  const [form, setForm] = useState<EpisodeFormState>(initial ?? emptyForm());

  const set = (k: keyof EpisodeFormState, v: string | boolean) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required.'); return; }
    if (!form.videoUrl.trim()) { toast.error('Video URL is required.'); return; }
    const s = parseInt(form.season);
    const ep = parseInt(form.episodeNumber);
    if (isNaN(s) || s < 1) { toast.error('Season must be a positive number.'); return; }
    if (isNaN(ep) || ep < 1) { toast.error('Episode number must be a positive number.'); return; }
    await onSubmit(form);
  };

  const fieldStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#ffffff',
    fontFamily: "'Barlow', sans-serif",
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Barlow', sans-serif",
    fontSize: '0.8rem',
    letterSpacing: '0.08em',
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {/* Row: Season + Episode number */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        <div>
          <label style={labelStyle}>Season</label>
          <Input
            type="number" min="1"
            value={form.season}
            onChange={e => set('season', e.target.value)}
            style={fieldStyle}
            placeholder="1"
          />
        </div>
        <div>
          <label style={labelStyle}>Episode Number</label>
          <Input
            type="number" min="1"
            value={form.episodeNumber}
            onChange={e => set('episodeNumber', e.target.value)}
            style={fieldStyle}
            placeholder="1"
          />
        </div>
      </div>

      {/* Title */}
      <div>
        <label style={labelStyle}>Title *</label>
        <Input
          value={form.title}
          onChange={e => set('title', e.target.value)}
          style={fieldStyle}
          placeholder="Episode title..."
          maxLength={120}
        />
      </div>

      {/* Description */}
      <div>
        <label style={labelStyle}>Description</label>
        <Textarea
          value={form.description}
          onChange={e => set('description', e.target.value)}
          style={{ ...fieldStyle, minHeight: '80px', resize: 'vertical' }}
          placeholder="Short episode description..."
        />
      </div>

      {/* Thumbnail URL */}
      <div>
        <label style={labelStyle}>Thumbnail URL</label>
        <Input
          value={form.thumbnailUrl}
          onChange={e => set('thumbnailUrl', e.target.value)}
          style={fieldStyle}
          placeholder="https://..."
          type="url"
        />
      </div>

      {/* Video URL */}
      <div>
        <label style={labelStyle}>Video URL * (YouTube or direct link)</label>
        <Input
          value={form.videoUrl}
          onChange={e => set('videoUrl', e.target.value)}
          style={fieldStyle}
          placeholder="https://youtube.com/watch?v=... or https://..."
          type="url"
        />
      </div>

      {/* Published toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => set('published', !form.published)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          {form.published ? (
            <ToggleRight size={28} style={{ color: '#7CFC00' }} />
          ) : (
            <ToggleLeft size={28} style={{ color: 'rgba(255,255,255,0.3)' }} />
          )}
          <span style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: '0.9rem',
            color: form.published ? '#7CFC00' : 'rgba(255,255,255,0.4)',
          }}>
            {form.published ? 'Published (visible on /series)' : 'Draft (hidden from /series)'}
          </span>
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
        <button
          type="submit"
          disabled={isLoading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: "'Bangers', cursive",
            fontSize: '1rem',
            letterSpacing: '0.1em',
            color: '#0a0f1a',
            background: isLoading ? 'rgba(124,252,0,0.4)' : '#7CFC00',
            border: '2px solid rgba(0,0,0,0.3)',
            padding: '8px 24px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
            transition: 'transform 0.1s ease',
          }}
        >
          <Save size={16} />
          {isLoading ? 'SAVING...' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: "'Bangers', cursive",
            fontSize: '1rem',
            letterSpacing: '0.1em',
            color: 'rgba(255,255,255,0.6)',
            background: 'transparent',
            border: '2px solid rgba(255,255,255,0.15)',
            padding: '8px 20px',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
          CANCEL
        </button>
      </div>
    </form>
  );
};

// ─── Episode Row ──────────────────────────────────────────────────────────────

const EpisodeRow: React.FC<{
  episode: EpisodesResponse;
  onEdit: (ep: EpisodesResponse) => void;
  onTogglePublished: (ep: EpisodesResponse) => void;
  onDelete: (ep: EpisodesResponse) => void;
}> = ({ episode, onEdit, onTogglePublished, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '14px 16px',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderLeft: episode.published ? '3px solid #7CFC00' : '3px solid rgba(255,255,255,0.1)',
      marginBottom: '8px',
      flexWrap: 'wrap',
    }}>
      {/* Season/Episode badge */}
      <div style={{
        fontFamily: "'Bangers', cursive",
        fontSize: '0.8rem',
        letterSpacing: '0.1em',
        color: '#7CFC00',
        background: 'rgba(124,252,0,0.08)',
        border: '1px solid rgba(124,252,0,0.2)',
        padding: '2px 10px',
        whiteSpace: 'nowrap',
      }}>
        S{episode.season}E{episode.episodeNumber}
      </div>

      {/* Title + description */}
      <div style={{ flex: 1, minWidth: '180px' }}>
        <div style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '1rem',
          letterSpacing: '0.05em',
          color: '#ffffff',
        }}>
          {episode.title}
        </div>
        {episode.description && (
          <div style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: '0.78rem',
            color: 'rgba(255,255,255,0.35)',
            marginTop: 2,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: '360px',
          }}>
            {episode.description}
          </div>
        )}
      </div>

      {/* Published status */}
      <div style={{ whiteSpace: 'nowrap' }}>
        {episode.published ? (
          <span style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: '0.75rem',
            color: '#7CFC00',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <Eye size={13} /> Published
          </span>
        ) : (
          <span style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.3)',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <EyeOff size={13} /> Draft
          </span>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {/* Toggle published */}
        <button
          onClick={() => onTogglePublished(episode)}
          title={episode.published ? 'Unpublish' : 'Publish'}
          style={{
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            color: episode.published ? '#7CFC00' : 'rgba(255,255,255,0.4)',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = '#7CFC00'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
        >
          {episode.published ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>

        {/* Edit */}
        <button
          onClick={() => onEdit(episode)}
          title="Edit"
          style={{
            width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.5)',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => {
            const el = e.currentTarget as HTMLElement;
            el.style.borderColor = 'rgba(255,255,255,0.4)';
            el.style.color = '#ffffff';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLElement;
            el.style.borderColor = 'rgba(255,255,255,0.1)';
            el.style.color = 'rgba(255,255,255,0.5)';
          }}
        >
          <Pencil size={14} />
        </button>

        {/* Delete */}
        {confirmDelete ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: '0.75rem', color: '#E63946' }}>
              Sure?
            </span>
            <button
              onClick={() => { onDelete(episode); setConfirmDelete(false); }}
              style={{
                padding: '4px 10px',
                background: '#E63946',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontFamily: "'Bangers', cursive",
                fontSize: '0.85rem',
                letterSpacing: '0.08em',
              }}
            >
              YES
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              style={{
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                fontFamily: "'Bangers', cursive",
                fontSize: '0.85rem',
                letterSpacing: '0.08em',
              }}
            >
              NO
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            title="Delete"
            style={{
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(230,57,70,0.08)',
              border: '1px solid rgba(230,57,70,0.2)',
              cursor: 'pointer',
              color: 'rgba(230,57,70,0.6)',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = 'rgba(230,57,70,0.18)';
              el.style.color = '#E63946';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.background = 'rgba(230,57,70,0.08)';
              el.style.color = 'rgba(230,57,70,0.6)';
            }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main Admin Page ──────────────────────────────────────────────────────────

type Mode = 'list' | 'add' | 'edit';

export const EpisodesAdminPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('list');
  const [editingEpisode, setEditingEpisode] = useState<EpisodesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'season' | 'created'>('season');

  const { data: episodes } = useRealtimeData<EpisodesResponse[]>(
    subscribeManyEpisodes,
    true
  );

  const sorted = [...(episodes ?? [])].sort((a, b) => {
    if (sortBy === 'season') {
      const sDiff = (a.season as number) - (b.season as number);
      if (sDiff !== 0) return sDiff;
      return (a.episodeNumber as number) - (b.episodeNumber as number);
    }
    return (b.createdAt as number) - (a.createdAt as number);
  });

  const publishedCount = (episodes ?? []).filter(ep => ep.published).length;

  const handleAdd = async (form: EpisodeFormState) => {
    setIsLoading(true);
    try {
      const id = generateId();
      const success = await setEpisodes(id, {
        title: form.title.trim(),
        description: form.description.trim(),
        thumbnailUrl: form.thumbnailUrl.trim(),
        videoUrl: form.videoUrl.trim(),
        season: parseInt(form.season),
        episodeNumber: parseInt(form.episodeNumber),
        published: form.published,
        createdAt: Time.Now,
      });
      if (success) {
        toast.success('Episode added!');
        setMode('list');
      } else {
        toast.error('Permission denied. Are you the admin?');
      }
    } catch (err) {
      toast.error('Failed to save episode.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (form: EpisodeFormState) => {
    if (!editingEpisode) return;
    setIsLoading(true);
    try {
      const success = await updateEpisodes(editingEpisode.id, {
        title: form.title.trim(),
        description: form.description.trim(),
        thumbnailUrl: form.thumbnailUrl.trim(),
        videoUrl: form.videoUrl.trim(),
        season: parseInt(form.season),
        episodeNumber: parseInt(form.episodeNumber),
        published: form.published,
      });
      if (success) {
        toast.success('Episode updated!');
        setMode('list');
        setEditingEpisode(null);
      } else {
        toast.error('Permission denied. Are you the admin?');
      }
    } catch (err) {
      toast.error('Failed to update episode.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePublished = async (ep: EpisodesResponse) => {
    const success = await updateEpisodes(ep.id, { published: !ep.published });
    if (success) {
      toast.success(ep.published ? 'Episode unpublished.' : 'Episode published!');
    } else {
      toast.error('Permission denied.');
    }
  };

  const handleDelete = async (ep: EpisodesResponse) => {
    const success = await deleteEpisodes(ep.id);
    if (success) {
      toast.success('Episode deleted.');
    } else {
      toast.error('Permission denied.');
    }
  };

  const startEdit = (ep: EpisodesResponse) => {
    setEditingEpisode(ep);
    setMode('edit');
  };

  const cancelForm = () => {
    setMode('list');
    setEditingEpisode(null);
  };

  const panelStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.07)',
    padding: '28px',
    marginBottom: '24px',
  };

  const sectionLabel: React.CSSProperties = {
    fontFamily: "'Bangers', cursive",
    fontSize: '1rem',
    letterSpacing: '0.2em',
    color: '#7CFC00',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  return (
    <PageLayout fullBleed>
      {/* Header */}
      <div style={{
        background: '#050a14',
        borderBottom: '1px solid rgba(124,252,0,0.12)',
        padding: '24px 0',
      }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '0.75rem',
                letterSpacing: '0.25em',
                color: '#7CFC00',
                marginBottom: '4px',
              }}>
                DANGER DAN ADMIN
              </div>
              <h1 style={{
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                letterSpacing: '0.05em',
                color: '#ffffff',
                lineHeight: 1,
              }}>
                EPISODE MANAGER
              </h1>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <a
                href="/series"
                style={{
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.9rem',
                  letterSpacing: '0.1em',
                  color: 'rgba(255,255,255,0.5)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  padding: '8px 16px',
                  textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.color = '#ffffff';
                  el.style.borderColor = 'rgba(255,255,255,0.4)';
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.color = 'rgba(255,255,255,0.5)';
                  el.style.borderColor = 'rgba(255,255,255,0.15)';
                }}
              >
                <Film size={14} />
                VIEW /SERIES
              </a>
              {mode === 'list' && (
                <button
                  onClick={() => setMode('add')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    fontFamily: "'Bangers', cursive",
                    fontSize: '1rem',
                    letterSpacing: '0.1em',
                    color: '#0a0f1a',
                    background: '#7CFC00',
                    border: '2px solid rgba(0,0,0,0.3)',
                    padding: '8px 22px',
                    cursor: 'pointer',
                    boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
                  }}
                >
                  <Plus size={16} />
                  ADD EPISODE
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: '#080d1f', minHeight: '100vh', padding: '40px 0' }}>
        <div className="container" style={{ maxWidth: '860px' }}>

          {/* Stats bar */}
          <div style={{
            display: 'flex', gap: 20, marginBottom: '32px', flexWrap: 'wrap',
          }}>
            {[
              { label: 'Total Episodes', value: (episodes ?? []).length },
              { label: 'Published', value: publishedCount },
              { label: 'Drafts', value: (episodes ?? []).length - publishedCount },
            ].map(stat => (
              <div
                key={stat.label}
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  padding: '12px 20px',
                  minWidth: 120,
                }}
              >
                <div style={{
                  fontFamily: "'Bangers', cursive",
                  fontSize: '1.6rem',
                  color: '#ffffff',
                  letterSpacing: '0.05em',
                  lineHeight: 1,
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.35)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginTop: 2,
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Admin note */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(230,57,70,0.06)',
            border: '1px solid rgba(230,57,70,0.15)',
            padding: '10px 16px',
            marginBottom: '28px',
          }}>
            <AlertTriangle size={14} style={{ color: '#E63946', flexShrink: 0 }} />
            <p style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.4)',
            }}>
              Write access is controlled by policy. Only the admin wallet can create, update, or delete episodes.
            </p>
          </div>

          {/* Add / Edit form */}
          {(mode === 'add' || mode === 'edit') && (
            <div style={{ ...panelStyle, marginBottom: '32px', borderColor: 'rgba(124,252,0,0.15)' }}>
              <div style={sectionLabel}>
                {mode === 'add' ? <Plus size={16} /> : <Pencil size={16} />}
                {mode === 'add' ? 'ADD NEW EPISODE' : 'EDIT EPISODE'}
              </div>
              <EpisodeForm
                initial={editingEpisode ? {
                  title: editingEpisode.title,
                  description: editingEpisode.description,
                  thumbnailUrl: editingEpisode.thumbnailUrl,
                  videoUrl: editingEpisode.videoUrl,
                  season: String(editingEpisode.season),
                  episodeNumber: String(editingEpisode.episodeNumber),
                  published: editingEpisode.published,
                } : undefined}
                onSubmit={mode === 'add' ? handleAdd : handleEdit}
                onCancel={cancelForm}
                submitLabel={mode === 'add' ? 'ADD EPISODE' : 'SAVE CHANGES'}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Episode list */}
          <div style={panelStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: 10 }}>
              <div style={sectionLabel}>
                <Film size={16} />
                ALL EPISODES
              </div>
              {/* Sort control */}
              <div style={{ display: 'flex', gap: 6 }}>
                {(['season', 'created'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setSortBy(s)}
                    style={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: '0.75rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      padding: '5px 12px',
                      cursor: 'pointer',
                      background: sortBy === s ? 'rgba(124,252,0,0.1)' : 'transparent',
                      border: `1px solid ${sortBy === s ? 'rgba(124,252,0,0.4)' : 'rgba(255,255,255,0.1)'}`,
                      color: sortBy === s ? '#7CFC00' : 'rgba(255,255,255,0.4)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {s === 'season' ? 'By Season' : 'By Date'}
                  </button>
                ))}
              </div>
            </div>

            {sorted.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                fontFamily: "'Barlow', sans-serif",
                color: 'rgba(255,255,255,0.25)',
              }}>
                No episodes yet. Add your first episode above.
              </div>
            ) : (
              sorted.map(ep => (
                <EpisodeRow
                  key={ep.id}
                  episode={ep}
                  onEdit={startEdit}
                  onTogglePublished={handleTogglePublished}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bangers&family=Barlow:wght@400;500;600;700&display=swap');
      `}</style>
    </PageLayout>
  );
};

export default EpisodesAdminPage;
